import React, {
    ReactElement,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import styles from './BackToTop.module.scss'
import { ArrowUpOutlined } from '@ant-design/icons'
import Tooltip from 'antd/lib/tooltip'
import { FadeInOut } from '@/components/static/FadeInOut/FadeInOut'
import { DEFAULT_ANIMATION_DURATION_MS } from '@/utils/reusableCssClasses'
import { useIsMobileMode } from '@/hooks/static/useIsMobileMode'

const minDistFromBottomPx = 20 + 72 // 20px default spacing, 72 for footer
export const BackToTop: React.FC<{ isLoggedIn: boolean }> = (
    props
): ReactElement => {
    const [isScrolled, setIsScrolled] = useState(false)

    const ref = useRef(null)
    const handleScroll = useCallback(() => {
        if (!ref.current) {
            return
        }
        const distanceFromBottom =
            document.documentElement.scrollHeight -
            (window.scrollY + window.innerHeight)
        if (distanceFromBottom < minDistFromBottomPx) {
            // 20 (initial offset from bottom) + minDistFromBottomPx
            ref.current.style.bottom = `${
                minDistFromBottomPx - distanceFromBottom
            }px`
        } else {
            ref.current.style.bottom = '20px'
        }
        if (window.scrollY > 0) {
            setIsScrolled(true)
        } else {
            setIsScrolled(false)
        }
    }, [])

    useEffect(() => {
        window.addEventListener('scroll', handleScroll)

        // Cleanup on component unmount
        return () => {
            window.removeEventListener('scroll', handleScroll)
        }
    }, [handleScroll])

    const handleClick = useCallback((): void => {
        setTimeout(() => setIsScrolling(true), DEFAULT_ANIMATION_DURATION_MS)
        setTimeout(() => setIsScrolling(false), 2_000)
        window.scrollTo({
            top: 0,
            behavior: 'smooth',
        })
    }, [])

    const [isScrolling, setIsScrolling] = useState<boolean>(false)

    const shouldShow = useMemo((): boolean => {
        return !isScrolling && isScrolled
    }, [isScrolled, isScrolling])
    useEffect(() => {
        handleScroll()
    }, [handleScroll])

    const isMobileMode = useIsMobileMode(props.isLoggedIn)
    if (isMobileMode) {
        return
    }
    return (
        <FadeInOut
            fadeInDurationInMs={DEFAULT_ANIMATION_DURATION_MS}
            fadeOutDurationInMs={DEFAULT_ANIMATION_DURATION_MS}
            isVisible={shouldShow}
        >
            <Tooltip placement={'right'} title={shouldShow && 'Back to top'}>
                <div
                    className={styles.backToTopButton}
                    onClick={handleClick}
                    ref={ref}
                >
                    <ArrowUpOutlined style={{ fontSize: 48 }} />
                </div>
            </Tooltip>
        </FadeInOut>
    )
}
